import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Check, Loader2, Sparkles, Code, Repeat, X, Building2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { AppShell, PageLoader } from "../components/AppShell";
import { api, formatError } from "../lib/api";

const PLAN_ICONS = { pro_monthly: Repeat, developer: Code, business_monthly: Building2 };

export default function SubscriptionPage() {
  const { refreshUser } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutId, setCheckoutId] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const [interval, setInterval] = useState("month");

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([
          api.get("/subscription/plans"),
          api.get("/subscription/status"),
        ]);
        setPlans(p.data);
        setStatus(s.data);
      } catch (err) {
        logger.error("Load subscription failed", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const subscribe = async (planId) => {
    setCheckoutId(planId);
    try {
      const { data } = await api.post("/subscription/checkout", { plan_id: planId, origin_url: window.location.origin, interval });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      logger.error("Subscribe error", err);
      toast.error(formatError(err.response?.data?.detail) || "Checkout failed");
    } finally {
      setCheckoutId(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel subscription? You'll keep access until the end of the billing period.")) return;
    setCanceling(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Subscription will cancel at period end");
      const { data } = await api.get("/subscription/status");
      setStatus(data);
      await refreshUser();
    } catch (err) {
      logger.error("Cancel error", err);
      toast.error(formatError(err.response?.data?.detail) || "Cancel failed");
    } finally {
      setCanceling(false);
    }
  };

  if (loading) return <PageLoader />;

  const hasActive = status?.status === "active" || status?.status === "canceling";

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8" data-testid="subscription-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-gradient-to-br from-brand to-purple-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-tighter text-zinc-950 dark:text-white font-heading">Monthly Plans</h1>
            <p className="text-sm text-zinc-500">Save 60%+ vs one-time credit packs. Cancel anytime.</p>
          </div>
        </div>

        {hasActive && (
          <div className="bg-white dark:bg-zinc-900 border border-brand/40 p-6 mb-8 animate-fade-in" data-testid="current-subscription">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Check className="w-5 h-5 text-green-600" />
                  <h2 className="text-lg font-bold text-zinc-900 dark:text-white font-heading">
                    {status.plan_name}
                    {status.status === "canceling" && <span className="ml-2 text-xs font-bold text-amber-600">CANCELING</span>}
                  </h2>
                </div>
                <p className="text-sm text-zinc-500">
                  ${status.amount}/{status.interval === "year" ? "yr" : "mo"} · Renews {status.current_period_end ? new Date(status.current_period_end).toLocaleDateString() : "automatically"}
                </p>
                {status.api_access && (
                  <button onClick={() => nav("/developer")} className="mt-3 text-sm font-semibold text-brand hover:underline flex items-center gap-1" data-testid="go-to-developer-btn">
                    <Code className="w-4 h-4" /> Manage API keys
                  </button>
                )}
              </div>
              {status.status !== "canceling" && (
                <button onClick={cancel} disabled={canceling} data-testid="cancel-subscription-btn" className="text-sm font-semibold text-red-600 hover:text-red-700 disabled:opacity-50 flex items-center gap-1">
                  {canceling ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  Cancel subscription
                </button>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-1" data-testid="billing-interval-toggle">
            {["month", "year"].map((v) => (
              <button
                key={v}
                onClick={() => setInterval(v)}
                data-testid={`interval-${v}`}
                className={`px-4 py-2 text-sm font-semibold transition-all ${interval === v ? "bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white shadow-sm" : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white"}`}
              >
                {v === "month" ? "Monthly" : "Annual"}
                {v === "year" && <span className="ml-2 text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5">SAVE 20%</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || Sparkles;
            const isCurrent = status?.tier === p.id && hasActive && status?.interval === interval;
            const displayAmount = interval === "year" ? p.annual_amount : p.monthly_amount;
            const displayCredits = interval === "year" ? p.annual_credits : p.credits;
            const perMonthEq = interval === "year" ? (p.annual_amount / 12).toFixed(2) : null;
            return (
              <div key={p.id} className={`border p-6 bg-white dark:bg-zinc-900 transition-all relative ${isCurrent ? "border-brand ring-2 ring-brand" : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-400"}`} data-testid={`sub-plan-${p.id}`}>
                {isCurrent && <div className="absolute -top-3 left-4 bg-brand text-white text-[10px] font-bold tracking-[0.15em] uppercase px-2 py-0.5">Active</div>}
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-5 h-5 text-brand" />
                  <h3 className="text-xl font-bold text-zinc-900 dark:text-white font-heading">{p.name}</h3>
                </div>
                <div className="text-4xl font-black text-zinc-950 dark:text-white tracking-tighter mb-1 font-heading">
                  ${displayAmount.toFixed(interval === "year" ? 2 : 0)}
                  <span className="text-base font-medium text-zinc-400">/{interval === "year" ? "yr" : "mo"}</span>
                </div>
                {interval === "year" && (
                  <p className="text-xs text-green-600 font-semibold mb-1" data-testid={`annual-savings-${p.id}`}>≈${perMonthEq}/mo effective · save 20%</p>
                )}
                <p className="text-sm text-zinc-500 mb-4">
                  {displayCredits} credits{interval === "year" ? " upfront for the year" : "/month (resets monthly)"}
                </p>
                <ul className="text-sm text-zinc-700 dark:text-zinc-300 space-y-2 mb-6">
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0" /> {displayCredits} credits {interval === "year" ? "(12 months worth)" : "every month"}</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0" /> All AI templates + Sora 2 video</li>
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0" /> Social toolkit (hashtags, captions, thumbnails)</li>
                  {p.api_access && <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0" /> <strong>Programmatic API access</strong></li>}
                  <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600 flex-shrink-0" /> Cancel anytime</li>
                </ul>
                <button
                  disabled={isCurrent || checkoutId === p.id}
                  onClick={() => subscribe(p.id)}
                  data-testid={`subscribe-${p.id}-btn`}
                  className={`w-full py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all ${isCurrent ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-500 cursor-not-allowed" : "bg-brand text-white hover:bg-brand-hover"}`}
                >
                  {checkoutId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrent ? "Current Plan" : `Subscribe ${interval === "year" ? "Annually" : "Monthly"}`}
                </button>
              </div>
            );
          })}
        </div>
        <div className="mt-6 flex items-start gap-2 text-xs text-zinc-500 bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Credits reset on each billing cycle. Unused credits don't roll over. This maximises our ability to offer low per-credit pricing. One-time packs remain available on the <button onClick={() => nav("/pricing")} className="underline font-semibold">Credits page</button>.</span>
        </div>
      </div>
    </AppShell>
  );
}
